// Runs `callback` exactly once, at the earliest of: the first real user
// interaction, the browser going idle, or a fallback timeout. Used to keep
// non-critical third-party and auth initialization out of the initial render
// path so it does not compete with first paint / LCP on slow devices — without
// dropping the work entirely (it still runs a moment later).
//
// Returns a cleanup function that cancels the pending trigger (call it from a
// React effect cleanup).

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

export function runWhenIdleOrInteraction(
  callback: () => void,
  options?: { timeout?: number },
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const win = window as IdleWindow;
  const timeout = options?.timeout ?? 3000;
  let done = false;
  let idleHandle: number | undefined;
  let timeoutHandle: number | undefined;

  const cleanup = () => {
    for (const event of INTERACTION_EVENTS) {
      window.removeEventListener(event, run);
    }
    if (idleHandle !== undefined && typeof win.cancelIdleCallback === 'function') {
      win.cancelIdleCallback(idleHandle);
    }
    if (timeoutHandle !== undefined) {
      window.clearTimeout(timeoutHandle);
    }
  };

  function run() {
    if (done) {
      return;
    }
    done = true;
    cleanup();
    callback();
  }

  for (const event of INTERACTION_EVENTS) {
    window.addEventListener(event, run, { once: true, passive: true });
  }

  if (typeof win.requestIdleCallback === 'function') {
    // requestIdleCallback with a timeout fires when idle, and is guaranteed to
    // fire by `timeout` even if the browser never goes idle.
    idleHandle = win.requestIdleCallback(run, { timeout });
  } else {
    timeoutHandle = window.setTimeout(run, timeout);
  }

  return cleanup;
}
